import { ReactNode, useState, useMemo } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { ChevronsUpDown, ChevronUp, ChevronDown, ChevronRight, ChevronDown as ChevronDownExpand } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface DataTableColumn<T> {
  key: keyof T
  header: string
  cell?: (value: any, row: T) => ReactNode
  exportValue?: (row: T) => string | number | null
  sortable?: boolean
  className?: string
  width?: string
  align?: 'left' | 'center' | 'right'
}

export interface DataTableProps<T> {
  data: T[]
  columns: DataTableColumn<T>[]
  searchValue?: string
  onRowClick?: (row: T) => void
  className?: string
  emptyMessage?: string
  emptyStateComponent?: ReactNode
  // External sort control (optional)
  sortConfig?: SortConfig<T>
  onSort?: (key: keyof T) => void
  // Expandable rows
  expandableContent?: (row: T) => ReactNode
  expandedRows?: Set<string | number>
  onRowExpand?: (rowId: string | number) => void
  getRowId?: (row: T, index: number) => string | number
  // Row styling
  getRowClassName?: (row: T) => string
}

type SortDirection = 'asc' | 'desc' | null

interface SortConfig<T> {
  key: keyof T | null
  direction: SortDirection
}

export function DataTable<T extends Record<string, any>>({
  data,
  columns,
  searchValue = '',
  onRowClick,
  className,
  emptyMessage = 'Inga resultat hittades',
  emptyStateComponent,
  sortConfig: externalSortConfig,
  onSort: externalOnSort,
  expandableContent,
  expandedRows = new Set(),
  onRowExpand,
  getRowId = (row, index) => index,
  getRowClassName
}: DataTableProps<T>) {
  const [internalSortConfig, setInternalSortConfig] = useState<SortConfig<T>>({
    key: null,
    direction: null
  })

  // Use external sort config if provided, otherwise use internal
  const sortConfig = externalSortConfig || internalSortConfig
  const onSort = externalOnSort || ((key: keyof T) => {
    setInternalSortConfig((prev) => {
      if (prev.key === key) {
        if (prev.direction === 'asc') return { key, direction: 'desc' }
        if (prev.direction === 'desc') return { key: null, direction: null }
      }
      return { key, direction: 'asc' }
    })
  })

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!searchValue) return data

    return data.filter((row) =>
      columns.some((column) => {
        const value = row[column.key]
        return value?.toString().toLowerCase().includes(searchValue.toLowerCase())
      })
    )
  }, [data, searchValue, columns])

  // Sort filtered data
  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) {
      return filteredData
    }

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key!]
      const bValue = b[sortConfig.key!]

      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      let comparison = 0
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue)
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue
      } else {
        comparison = String(aValue).localeCompare(String(bValue))
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison
    })
  }, [filteredData, sortConfig])


  const getSortIcon = (key: keyof T) => {
    if (sortConfig.key !== key || !sortConfig.direction) {
      return null
    }
    if (sortConfig.direction === 'asc') {
      return <ChevronUp className="h-4 w-4 ml-1" />
    }
    if (sortConfig.direction === 'desc') {
      return <ChevronDown className="h-4 w-4 ml-1" />
    }
    return null
  }

  return (
    <div className={cn('rounded-md border overflow-hidden max-w-full', className)}>
      <div className="overflow-x-auto">
        <Table className="min-w-full">
          <TableHeader>
            <TableRow>
              {expandableContent && (
                <TableHead style={{ width: '40px' }} />
              )}
              {columns.map((column, colIndex) => (
                <TableHead
                  key={`${String(column.key)}-${colIndex}`}
                  className={cn(
                    column.className,
                    column.align === 'center' && 'text-center',
                    column.align === 'right' && 'text-right'
                  )}
                  style={column.width ? { width: column.width } : undefined}
                >
                  {column.sortable ? (
                    <div
                      className={cn(
                        "flex items-center cursor-pointer hover:bg-muted/50",
                        column.align === 'center' && 'justify-center',
                        column.align === 'right' && 'justify-end'
                      )}
                      onClick={() => onSort(column.key)}
                    >
                      {column.header}
                      {getSortIcon(column.key)}
                    </div>
                  ) : (
                    <div className={cn(
                      column.align === 'center' && 'text-center',
                      column.align === 'right' && 'text-right'
                    )}>
                      {column.header}
                    </div>
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.length > 0 ? (
              sortedData.flatMap((row, index) => {
                const rowId = getRowId(row, index)
                const isExpanded = expandedRows.has(rowId)
                const rowElements = []

                // Main row
                const baseClassName = onRowClick ? 'cursor-pointer hover:bg-muted/50' : ''
                const customClassName = getRowClassName ? getRowClassName(row) : ''
                const combinedClassName = cn(baseClassName, customClassName)

                rowElements.push(
                  <TableRow
                    key={`row-${rowId}`}
                    className={combinedClassName}
                    onClick={() => onRowClick?.(row)}
                  >
                    {expandableContent && (
                      <TableCell
                        className="w-10 p-2"
                        onClick={(e) => {
                          e.stopPropagation()
                          onRowExpand?.(rowId)
                        }}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 hover:bg-transparent"
                        >
                          {isExpanded ? (
                            <ChevronDownExpand className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                    )}
                    {columns.map((column, colIndex) => {
                      const value = row[column.key]
                      return (
                        <TableCell
                          key={`${String(column.key)}-${colIndex}`}
                          className={cn(
                            column.className,
                            column.align === 'center' && 'text-center',
                            column.align === 'right' && 'text-right'
                          )}
                        >
                          {column.cell ? column.cell(value, row) : value}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )

                // Expanded row
                if (expandableContent && isExpanded) {
                  rowElements.push(
                    <TableRow key={`expanded-${rowId}`}>
                      <TableCell colSpan={columns.length + 1} className="p-0">
                        <div className="px-4 py-3 bg-muted/30 border-t">
                          {expandableContent(row)}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                }

                return rowElements
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (expandableContent ? 1 : 0)}
                  className={emptyStateComponent ? "p-0" : "text-center text-muted-foreground py-8"}
                >
                  {emptyStateComponent || emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}